// 🛑 RECTIFICATION : Composant Collecte Documents Post-Audit
// Documents dynamiques selon les choix (Résilier/Optimiser/Déménagement)
import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, CheckCircle, AlertTriangle, Loader2, X,
  Zap, Phone, Wifi, Shield, Play, CreditCard, Home, User,
  File, Trash2, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API } from '../../App';

// Types de documents requis par action
const DOCUMENT_REQUIREMENTS = {
  resilier: {
    label: 'Résiliation',
    icon: Trash2,
    color: '#EF4444',
    docs: [
      { type: 'contract', label: 'Dernier contrat ou facture', required: true, description: 'Pour identifier le compte à résilier' }
    ]
  },
  optimiser: {
    label: 'Nouveau contrat',
    icon: Zap,
    color: '#00F5FF',
    docs: [
      { type: 'id', label: 'Pièce d\'identité', required: true, description: 'CNI, passeport ou titre de séjour' },
      { type: 'rib', label: 'RIB', required: true, description: 'Pour le prélèvement automatique' }
    ]
  },
  demenagement: {
    label: 'Déménagement',
    icon: Home,
    color: '#10B981',
    docs: [
      { type: 'justificatif', label: 'Justificatif de domicile', required: true, description: 'Moins de 3 mois (facture, bail...)' }
    ]
  }
};

// Composant d'upload individuel
const DocumentUploadCard = ({ doc, onUpload, onRemove, uploadedFile, uploading }) => {
  const [dragOver, setDragOver] = useState(false);
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onUpload(doc.type, file);
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) onUpload(doc.type, file);
  };
  
  return (
    <div 
      className={`
        border-2 border-dashed rounded-lg p-4 transition-all
        ${dragOver ? 'border-cyan-400 bg-cyan-400/10' : 'border-gray-700 bg-gray-800/30'}
        ${uploadedFile ? 'border-success/50 bg-success/5' : ''}
      `}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-gray-500" />
            <span className="text-white text-sm font-medium">{doc.label}</span>
            {doc.required && (
              <span className="text-red-400 text-xs">*</span>
            )}
          </div>
          <p className="text-gray-500 text-xs">{doc.description}</p>
          
          {/* Fichier uploadé */}
          {uploadedFile && (
            <div className="mt-2 flex items-center gap-2 p-2 bg-success/10 rounded">
              <CheckCircle className="w-4 h-4 text-success" />
              <span className="text-success text-xs truncate flex-1">{uploadedFile.name}</span>
              <button 
                onClick={() => onRemove(doc.type)}
                className="p-1 hover:bg-red-500/20 rounded transition-colors"
              >
                <X className="w-3 h-3 text-red-400" />
              </button>
            </div>
          )}
        </div>
        
        {/* Zone d'upload */}
        {!uploadedFile && (
          <label className="cursor-pointer">
            <input 
              type="file" 
              className="hidden" 
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <div className={`
              px-3 py-2 rounded-lg transition-colors flex items-center gap-2
              ${uploading ? 'bg-gray-700 cursor-wait' : 'bg-cyan-400/20 hover:bg-cyan-400/30'}
            `}>
              {uploading ? (
                <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
              ) : (
                <>
                  <Upload className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-400 text-xs">Ajouter</span>
                </>
              )}
            </div>
          </label>
        )}
      </div>
    </div>
  );
};

// 🛑 RECTIFICATION : Composant principal Collecte Documents
const DocumentCollectionStep = ({ 
  auditChoices = [], // Choix du Plan d'Action (resilier, optimiser, garder)
  hasDemenagement = false,
  onComplete,
  onBack
}) => {
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploading, setUploading] = useState({});
  const [globalLoading, setGlobalLoading] = useState(false);
  
  // Déterminer les documents requis dynamiquement
  const requiredDocs = useMemo(() => {
    const docs = [];
    
    // Documents de résiliation
    const resilierChoices = auditChoices.filter(c => c.choice === 'resilier');
    if (resilierChoices.length > 0) {
      docs.push({
        category: 'resilier',
        ...DOCUMENT_REQUIREMENTS.resilier,
        items: resilierChoices.map(c => c.creditor)
      });
    }
    
    // Documents pour optimisation (nouveau contrat)
    const optimiserChoices = auditChoices.filter(c => c.choice === 'optimiser');
    if (optimiserChoices.length > 0) {
      docs.push({
        category: 'optimiser',
        ...DOCUMENT_REQUIREMENTS.optimiser,
        items: optimiserChoices.map(c => c.creditor)
      });
    }
    
    // Documents de déménagement
    if (hasDemenagement) {
      docs.push({
        category: 'demenagement',
        ...DOCUMENT_REQUIREMENTS.demenagement,
        items: ['Transfert de contrats']
      });
    }
    
    return docs;
  }, [auditChoices, hasDemenagement]);
  
  // Calculer la progression
  const progress = useMemo(() => {
    let required = 0;
    let uploaded = 0;
    
    requiredDocs.forEach(category => {
      category.docs.forEach(doc => {
        if (doc.required) {
          required++;
          if (uploadedDocs[`${category.category}_${doc.type}`]) {
            uploaded++;
          }
        }
      });
    });
    
    return { required, uploaded, percent: required > 0 ? Math.round((uploaded / required) * 100) : 100 };
  }, [requiredDocs, uploadedDocs]);
  
  const allRequiredUploaded = progress.uploaded >= progress.required;
  
  // Handlers
  const handleUpload = async (category, docType, file) => {
    const key = `${category}_${docType}`;
    setUploading(prev => ({ ...prev, [key]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', docType);
      formData.append('category', category);
      
      const token = localStorage.getItem('token');
      
      // 🔴 P0 : Utiliser l'API réelle d'upload de documents
      const res = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      
      if (res.data.document_id) {
        setUploadedDocs(prev => ({
          ...prev,
          [key]: { 
            name: file.name, 
            size: file.size, 
            uploadedAt: new Date().toISOString(),
            document_id: res.data.document_id
          }
        }));
        toast.success(`${file.name} uploadé avec succès`);
      } else {
        throw new Error('Upload incomplet');
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload du fichier');
    } finally {
      setUploading(prev => ({ ...prev, [key]: false }));
    }
  };
  
  const handleRemove = (category, docType) => {
    const key = `${category}_${docType}`;
    setUploadedDocs(prev => {
      const newDocs = { ...prev };
      delete newDocs[key];
      return newDocs;
    });
    toast.info('Fichier supprimé');
  };
  
  const handleValidate = async () => {
    if (!allRequiredUploaded) {
      toast.error('Veuillez fournir tous les documents obligatoires');
      return;
    }
    
    setGlobalLoading(true);
    try {
      // Sauvegarder que les documents sont complets
      const token = localStorage.getItem('token');
      await axios.post(`${API}/audit-actions/save-choices`, 
        { 
          choices: auditChoices,
          documents_complete: true 
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      toast.success('Documents validés ! Vous pouvez maintenant procéder au paiement.');
      if (onComplete) onComplete();
    } catch (error) {
      console.error('Erreur validation:', error);
      toast.error('Erreur lors de la validation');
    } finally {
      setGlobalLoading(false);
    }
  };
  
  // Si aucun document requis
  if (requiredDocs.length === 0) {
    return (
      <div className="glass-card p-6 border border-success/30 text-center">
        <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
        <h3 className="font-orbitron text-lg text-white mb-2">Aucun document requis</h3>
        <p className="text-gray-400 text-sm mb-4">
          Vous n'avez conservé aucun abonnement nécessitant des justificatifs.
        </p>
        <Button onClick={onComplete} className="bg-success text-black hover:bg-success/90">
          Passer au paiement
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header avec progression */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-4 sm:p-6 border border-cyan-400/30"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-cyan-400/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h2 className="font-orbitron text-lg font-bold text-white">Documents Requis</h2>
              <p className="text-gray-400 text-sm">Pour finaliser vos demandes</p>
            </div>
          </div>
          
          {/* Barre de progression */}
          <div className="flex items-center gap-3">
            <div className="w-32 h-2 bg-gray-800 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-gradient-to-r from-cyan-400 to-success"
                initial={{ width: 0 }}
                animate={{ width: `${progress.percent}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <span className={`text-sm font-orbitron ${progress.percent === 100 ? 'text-success' : 'text-gray-400'}`}>
              {progress.uploaded}/{progress.required}
            </span>
          </div>
        </div>
        
        {/* Info */}
        <div className="mt-4 p-3 bg-cyan-400/5 border border-cyan-400/20 rounded-lg">
          <p className="text-cyan-400 text-xs flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span>Les documents marqués d'un <span className="text-red-400">*</span> sont obligatoires pour procéder au paiement.</span>
          </p>
        </div>
      </motion.div>
      
      {/* Sections de documents par catégorie */}
      {requiredDocs.map((category, idx) => {
        const Icon = category.icon;
        return (
          <motion.div
            key={category.category}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="glass-card p-4 sm:p-6 border border-gray-700"
          >
            {/* Header catégorie */}
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-700">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: category.color + '20' }}
              >
                <Icon className="w-5 h-5" style={{ color: category.color }} />
              </div>
              <div>
                <h3 className="font-orbitron text-base text-white">{category.label}</h3>
                <p className="text-gray-500 text-xs">
                  Pour : {category.items.slice(0, 3).join(', ')}
                  {category.items.length > 3 && ` et ${category.items.length - 3} autres`}
                </p>
              </div>
            </div>
            
            {/* Documents */}
            <div className="space-y-3">
              {category.docs.map((doc) => (
                <DocumentUploadCard
                  key={`${category.category}_${doc.type}`}
                  doc={doc}
                  uploadedFile={uploadedDocs[`${category.category}_${doc.type}`]}
                  uploading={uploading[`${category.category}_${doc.type}`]}
                  onUpload={(type, file) => handleUpload(category.category, type, file)}
                  onRemove={(type) => handleRemove(category.category, type)}
                />
              ))}
            </div>
          </motion.div>
        );
      })}
      
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        {onBack && (
          <Button 
            variant="outline" 
            onClick={onBack}
            className="border-gray-600 text-gray-400 hover:text-white"
          >
            Retour au Plan d'Action
          </Button>
        )}
        <Button 
          onClick={handleValidate}
          disabled={!allRequiredUploaded || globalLoading}
          className={`flex-1 font-orbitron ${
            allRequiredUploaded 
              ? 'bg-success text-black hover:bg-success/90' 
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          {globalLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Valider et passer au paiement
              <ChevronRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export { DocumentCollectionStep, DOCUMENT_REQUIREMENTS };
export default DocumentCollectionStep;
